// ====================================================================
// COMMUNICATION AND NOTIFICATION ENTITIES
// ====================================================================

/**
 * Message thread for group or direct messaging
 */
export interface MessageThread {
  id: string;
  subject?: string;
  threadType: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  threadParticipants?: ThreadParticipant[];
  threadMessages?: ThreadMessage[];
}

/**
 * Participant in a message thread
 */
export interface ThreadParticipant {
  id: number;
  threadId: string;
  userId: string;
  joinedAt: string;
  leftAt?: string;
  isActive: boolean;
}

/**
 * Individual message within a thread
 */
export interface ThreadMessage {
  id: string;
  threadId: string;
  actionTypeId?: number;
  actionType?: MessageActionType;
  senderId: string;
  messageContent: string;
  messageType: string;
  fileUrl?: string;
  isEdited: boolean;
  editedAt?: string;
  createdAt: string;
  senderName?: string;
  senderAvatar?: string;
}

/**
 * Action types for messages (e.g., shift acceptance, document upload)
 */
export interface MessageActionType {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

/**
 * Email template types
 */
export enum EmailTemplateTypeEnum {
  Welcome = 'Welcome',
  PasswordReset = 'PasswordReset',
  ShiftConfirmation = 'ShiftConfirmation',
  ShiftReminder = 'ShiftReminder',
  PaymentNotification = 'PaymentNotification',
  DocumentRequest = 'DocumentRequest',
  AccountVerification = 'AccountVerification',
  ContractorInvitation = 'ContractorInvitation',
  FacilityInvitation = 'FacilityInvitation',
  ShiftCancellation = 'ShiftCancellation',
  Custom = 'Custom'
}

/**
 * Email delivery status
 */
export enum EmailStatusEnum {
  Queued = 'Queued',
  Sent = 'Sent',
  Delivered = 'Delivered',
  Opened = 'Opened',
  Clicked = 'Clicked',
  Bounced = 'Bounced',
  Failed = 'Failed',
  Complained = 'Complained'
}

/**
 * Email log for audit trail and analytics
 */
export interface EmailLog {
  id: string;
  userId?: string;
  recipientEmail: string;
  subject: string;
  templateType: EmailTemplateTypeEnum;
  templateTypeDisplay?: string;
  status: EmailStatusEnum;
  statusDisplay?: string;
  ghlContactId?: string;
  ghlMessageId?: string;
  ghlConversationId?: string;
  ghlTraceId?: string;
  errorMessage?: string;
  retryCount: number;
  hangfireJobId?: string;
  createdAt: string;
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
  clickedAt?: string;
  bouncedAt?: string;
  complainedAt?: string;
  openCount: number;
  clickCount: number;
  metadata?: string;
}

/**
 * Email template for sending emails
 */
export interface EmailTemplate {
  id: number;
  templateName: string;
  subjectTemplate: string;
  bodyTemplate: string;
  templateVariables?: string[];
  isHtml: boolean;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Push notification token for mobile devices
 */
export interface PushNotificationToken {
  id: string;
  userId: string;
  token: string;
  platform: string;
  deviceId?: string;
  deviceName?: string;
  isActive: boolean;
  lastUsed: string;
  createdAt: string;
}

/**
 * System announcement for all users or specific roles
 */
export interface SystemAnnouncement {
  id: string;
  title: string;
  content: string;
  announcementType: string;
  priority: number;
  isActive: boolean;
  startsAt: string;
  endsAt?: string;
  createdBy: string;
  createdAt: string;
  targetRoles?: AnnouncementTargetRole[];
}

/**
 * Target roles for system announcements
 */
export interface AnnouncementTargetRole {
  id: number;
  announcementId: string;
  targetRole: string;
  createdAt: string;
}

// ====================================================================
// CONTENT AND RESOURCES ENTITIES
// ====================================================================

/**
 * Resource type for contractor resources
 */
export enum ResourceTypeEnum {
  Document = 'document',
  Video = 'video',
  Link = 'link',
  Article = 'article',
  Other = 'other'
}

/**
 * Resource for contractors (training materials, guides, etc.)
 */
export interface ContractorResource {
  id: string;
  title: string;
  resourceType: string;
  fileUrl?: string;
  content?: string;
  category?: string;
  description?: string;
  fileSizeBytes?: number;
  mimeType?: string;
  isPublished: boolean;
  uploadedBy: string;
  uploadedByName?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * FAQ entry for help and support
 */
export interface FaqEntry {
  id: number;
  question: string;
  answer: string;
  category?: string;
  orderIndex: number;
  isPublished: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ====================================================================
// DTOs FOR API REQUESTS/RESPONSES
// ====================================================================

/**
 * DTO for creating a new message thread
 */
export interface CreateMessageThreadDto {
  subject?: string;
  threadType: string;
  participantIds: string[];
  initialMessage?: string;
}

/**
 * DTO for sending a message in a thread
 */
export interface SendMessageDto {
  threadId: string;
  messageContent: string;
  messageType?: string;
  fileUrl?: string;
}

/**
 * DTO for creating a system announcement
 */
export interface CreateAnnouncementDto {
  title: string;
  content: string;
  announcementType: string;
  priority: number;
  startsAt: string;
  endsAt?: string;
  targetRoles: string[];
}

/**
 * DTO for sending an email
 */
export interface SendEmailDto {
  recipientEmail: string;
  subject: string;
  templateType: EmailTemplateTypeEnum;
  templateVariables?: Record<string, string>;
}

/**
 * DTO for bulk email sending
 */
export interface BulkEmailDto {
  recipientEmails: string[];
  subject: string;
  templateType: EmailTemplateTypeEnum;
  templateVariables?: Record<string, string>;
}

/**
 * DTO for creating a contractor resource
 */
export interface CreateResourceDto {
  title: string;
  resourceType: string;
  fileUrl?: string;
  content?: string;
  category?: string;
  description?: string;
  fileSizeBytes?: number;
  mimeType?: string;
}

/**
 * DTO for creating/updating FAQ entry
 */
export interface CreateFaqDto {
  question: string;
  answer: string;
  category?: string;
  orderIndex?: number;
  isPublished?: boolean;
}

/**
 * Communication statistics for analytics
 */
export interface CommunicationStats {
  totalMessages: number;
  totalThreads: number;
  unreadMessages: number;
  activeThreads: number;
  totalEmailsSent: number;
  emailDeliveryRate: number;
  emailOpenRate: number;
  emailClickRate: number;
}
